import React from 'react'
import {IntlShape} from 'react-intl'
import {FacetFilter} from '@findhotel/sapi'
import {searchParamToFacet} from 'modules/search/constants'
import {
  FilterOptionItem,
  FilterUrlParam,
  FilterUrlParams
} from 'modules/search/types'
import {equals, isEmpty, isNil, path} from 'ramda'
import {FacetType} from 'types/Search'
import {toArray} from 'utils/object'

import {Lov} from '@vio/core/src/analytics/types/Events'
import {FilterId} from '@vio/core/src/search/types'

import {FacetGroup, FacetGroupItem} from './FacetFilter'

import type {FacetsAsArray} from '@vio/core/src/sapi/types'

interface PopularFilterProps {
  isActive?: boolean
  handleSingleFilterChange: (value: FilterUrlParams) => void
  listOfValues: Lov
  toggleMultiFilter: (
    filterKey: keyof FilterUrlParams,
    filterValue: string | null | undefined
  ) => void
  facets: FacetsAsArray
}
export interface IQuickFilter<T = object> {
  id: FilterId
  name: string | string[]
  Component: React.FC<T>
  activeCondition?: {
    name: string
    value: string | string[]
  }
}
export type PopularFilter = Omit<IQuickFilter<PopularFilterProps>, 'id'> & {
  id: string
}

/** Helper to build a dynamic data-id used to anchor the scroll item */
export const buildDataId = (key: string) => `all-filters-item-${key}`

/** Helper to get all available filter keys */
export const getAllFilterKeys = () => {
  const {
    reviewScore,
    starRatings,
    themes,
    facilities,
    propertyTypes,
    priceMin,
    priceMax
  } = searchParamToFacet

  return [
    'freeCancellation',
    'payLater',
    'exclusive',
    reviewScore.param,
    starRatings.param,
    themes.param,
    facilities.param,
    propertyTypes.param,
    priceMin.param,
    priceMax.param,
    'sortField',
    'sortOrder',
    'amenities',
    'brandIds',
    'neighborhoodIds',
    'dealFreeze' // Added as a part of c747927f-deal-freeze-srp
  ]
}

// SAPI only returns valid filters. This function allows users to remove filters
// that are already applied individually instead of clearing all of them
export const getValidFacets = (
  facets: FacetType[],
  appliedFilters: FilterUrlParam
) => {
  const facetsAreValid = !isNil(facets) && !isEmpty(facets)

  // if facets are empty and no filters are applied

  if (!facetsAreValid && !appliedFilters) {
    return []
  }
  // or facets are not empty (valid)
  if (facetsAreValid) return facets

  if (typeof appliedFilters === 'string') {
    // return facets array based on already applied filter
    return [{id: appliedFilters, value: +appliedFilters}]
  }

  // return facets array based on already applied filters
  return (appliedFilters as string[]).map((k: string) => {
    return {id: k, value: +k}
  })
}

/**
 * Group the facets based on the valid facets and facet groups provided.
 * @param validFacets - The valid facets to filter.
 * @param facetGroups - The facet groups to process.
 * @returns The grouped facets based on the facet groups.
 */
export const getValidFacetGroups = (
  validFacets: FacetType[],
  facetGroups: FacetGroupItem[]
): FacetGroup =>
  facetGroups?.reduce((acc, group) => {
    return {
      ...acc,
      [group.type]: validFacets?.filter(facet =>
        group?.facetIds?.has(facet?.id)
      )
    }
  }, {})

export const getFiltersLabel = (
  categoryType: string,
  facetId: string,
  listOfValues: Lov
) => {
  return path([`${categoryType}:${facetId}`, 'value'], listOfValues)
}

export const isFacetValid = (
  facetId: string,
  facetKey: 'themeIds' | 'facilities',
  facets: FacetsAsArray
) => {
  return Boolean(facets?.[facetKey]?.find(({id}) => id === facetId))
}
export const isQuickFilterActive = (
  filter: PopularFilter,
  appliedFilters: FilterUrlParams
): boolean => {
  // If the filter has no active condition
  if (filter.activeCondition) {
    // If both the current value and the applied filter value are arrays
    if (
      Array.isArray(filter.activeCondition.value) &&
      Array.isArray(appliedFilters[filter.activeCondition.name])
    ) {
      // Check if the sets of values are equal
      return equals(
        new Set(appliedFilters[filter.activeCondition.name]),
        new Set(filter.activeCondition.value)
      )
    }

    // If the applied filter value is an array and we want to check for a single value
    if (Array.isArray(appliedFilters[filter.activeCondition.name])) {
      // Check if the value is included in the applied filter array
      return appliedFilters[filter.activeCondition.name].includes(
        filter.activeCondition.value
      )
    }

    // If the applied filter is a single value
    return (
      appliedFilters[filter.activeCondition.name] ===
      filter.activeCondition.value
    )
  }

  // If the filter has an active condition and its name is an array
  if (Array.isArray(filter.name)) {
    // Check if at least one of the filter names is truthy in applied filters
    return filter.name.some(filterName => appliedFilters[filterName])
  }

  // If the filter has an active condition and its name is a single value
  return Boolean(appliedFilters[filter.name])
}

export const getOrderedActiveQuickFilters = (
  quickFilters: PopularFilter[],
  appliedFilters: FilterUrlParams,
  prevFilters: string[]
) => {
  const newActiveFilters = [...prevFilters]

  for (const filter of quickFilters) {
    if (
      isQuickFilterActive(filter, appliedFilters) &&
      !prevFilters.includes(filter.id)
    ) {
      newActiveFilters.unshift(filter.id)
    }

    if (
      !isQuickFilterActive(filter, appliedFilters) &&
      prevFilters.includes(filter.id)
    ) {
      newActiveFilters.splice(newActiveFilters.indexOf(filter.id), 1)
    }
  }

  return newActiveFilters
}

/**
 * Get the list of filters for a facet.
 * @param filters - The filters to display
 * @param isExpandable - Whether the filters are expandable
 * @param isExpanded - Whether the filters are expanded
 * @param limit - The limit of filters to display
 * @returns The list of filters for the facet
 */
export const getVisibleFacetFilters = (
  filters: FacetFilter[],
  isExpandable: boolean,
  isExpanded: boolean,
  limit: number
) => {
  if (isNil(filters)) {
    return []
  }

  if (!isExpandable) {
    return filters
  }

  return filters.slice(0, isExpanded ? undefined : limit)
}

/**
 * Get the label for a facet filter.
 * @param filters - The filters to display
 * @param appliedFilters - The applied filters to get the label for
 * @param defaultMessage - The default message to display if the filters are null or undefined
 * @returns The label for the facet filter
 */
export const getFacetFilterPillLabel = (
  filters: FacetFilter[],
  appliedFilters: string[],
  defaultMessage: string
): string | undefined => {
  if (appliedFilters.length === 0 || isNil(appliedFilters) || isNil(filters)) {
    return defaultMessage
  }

  if (appliedFilters.length === 1) {
    return filters.find(
      neighborhood => neighborhood.id === appliedFilters.toString()
    )?.value
  }

  const latestFilter = filters.find(
    filter => filter.id === appliedFilters[appliedFilters.length - 1]
  )?.value

  if (!latestFilter) return undefined

  return `${filters.find(filter => filter.id === appliedFilters[appliedFilters.length - 1])?.value} +${
    appliedFilters.length - 1
  }`
}

/**
 * Builds a formatted price label based on minimum and maximum price values.
 
 */
interface GetPriceActiveLabelParams {
  minPrice: number | undefined
  maxPrice: number | undefined
  currencySymbol: string
  formatMessage: IntlShape['formatMessage']
}
export const getPriceActiveLabel = ({
  minPrice,
  maxPrice,
  currencySymbol,
  formatMessage
}: GetPriceActiveLabelParams) => {
  const hasMin = Boolean(minPrice)
  const hasMax = Boolean(maxPrice)

  const minPriceWithCurrency = `${currencySymbol}${minPrice}`
  const maxPriceWithCurrency = `${currencySymbol}${maxPrice}`

  if (hasMin && hasMax) {
    return `${minPriceWithCurrency} - ${maxPriceWithCurrency}`
  }
  if (hasMin) {
    return formatMessage(
      {
        id: 'priceFrom',
        defaultMessage: 'From {price}'
      },
      {
        price: minPriceWithCurrency
      }
    )
  }
  if (hasMax) {
    return formatMessage(
      {
        id: 'priceUpTo',
        defaultMessage: 'Up to {price}'
      },
      {
        price: maxPriceWithCurrency
      }
    )
  }
}

/**
 * Checks if a specific filter key is selected
 */
export const getIsFilterSelected = (
  filters: FilterUrlParam,
  filterKey: string
): boolean => {
  const filtersArray = toArray(filters)
  const isSelectedFilter = filtersArray?.includes(filterKey)

  if (isSelectedFilter) return true
  return false
}

export interface RecentlyUsedFilter {
  id: string
  type: keyof FilterUrlParams
  value: string
}

const byLabel = (a: FilterOptionItem, b: FilterOptionItem) =>
  a.label.localeCompare(b.label)

export const getSortedFilters = (
  filterOptions: FilterOptionItem[],
  appliedFilters?: FilterUrlParam,
  shouldShowUnavailableFilters?: boolean
) => {
  if (!appliedFilters) {
    if (shouldShowUnavailableFilters) {
      return [...filterOptions].sort(byLabel)
    }
    return [...filterOptions].filter(f => f.isAvailable).sort(byLabel)
  }

  const selectedFilters: FilterOptionItem[] = []
  const unselectedAvailableFilters: FilterOptionItem[] = []
  const unselectedNotAvailableFilters: FilterOptionItem[] = []

  for (const filter of filterOptions) {
    if (getIsFilterSelected(appliedFilters, filter.id)) {
      selectedFilters.push(filter)
    } else if (filter.isAvailable) {
      unselectedAvailableFilters.push(filter)
    } else {
      unselectedNotAvailableFilters.push(filter)
    }
  }

  selectedFilters.sort(byLabel)
  unselectedAvailableFilters.sort(byLabel)
  unselectedNotAvailableFilters.sort(byLabel)

  return [
    ...selectedFilters,
    ...unselectedAvailableFilters,
    ...unselectedNotAvailableFilters
  ]
}

/**
 * Returns a limited number of filters
 * @param filters - The filters to limit
 * @param isLimited - Whether the filters are limited
 * @param limit - The limit to apply
 * @returns The limited filters
 */
export const getLimitedFilters = (
  filters: FilterOptionItem[],
  isLimited: boolean,
  limit: number,
  appliedFilters?: FilterUrlParam,
  shouldShowUnavailableFilters?: boolean
) => {
  if (!filters || filters.length === 0) return []
  if (!isLimited || !limit)
    return getSortedFilters(
      filters,
      appliedFilters,
      shouldShowUnavailableFilters
    )
  const sortedFilters = getSortedFilters(
    filters,
    appliedFilters,
    shouldShowUnavailableFilters
  )
  return sortedFilters.slice(0, limit)
}
